import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { Inspection } from '@/designs-dev/inspect/fiber';

// The stage's interaction mode: plain preview, the inspect/audit drill, or
// commenting. `active` (drill chrome on) is just `mode === 'inspect'`; comment
// mode has its own layer and panel (designs-dev/comments).
export type InspectMode = 'view' | 'inspect' | 'comment';

type InspectState = {
  mode: InspectMode;
  setMode: (m: InspectMode) => void;
  // Derived: the drill chrome (overlay + side panel) shows in inspect.
  active: boolean;
  // setActive(true) enters inspect; setActive(false) returns to view.
  setActive: (v: boolean) => void;
  // The committed selection — changes ONLY on click (canvas or layers panel).
  // Drives the detail panel and the strong highlight.
  current: Inspection | null;
  setCurrent: (i: Inspection | null) => void;
  // Transient hover preview — set on hover (canvas or panel), drives a light
  // preview highlight only. Never changes `current`, so hovering never commits.
  hovered: Inspection | null;
  setHovered: (i: Inspection | null) => void;
  // The phone's screen element, registered by InspectOverlay so panels living
  // outside the frame (the layers panel) can read the same component tree.
  screenEl: HTMLElement | null;
  setScreenEl: (el: HTMLElement | null) => void;
  // Whether the layers tree is showing the layout scaffolding as well as the
  // named components. Lifted out of the layers panel because it now gates more
  // than that tree: tweaking is only offered in the full view, since half the
  // things worth moving are the Stacks and Boxes the curated view leaves out.
  showLayout: boolean;
  setShowLayout: (v: boolean) => void;
};

const InspectContext = createContext<InspectState | null>(null);

export const useInspect = () => {
  const ctx = useContext(InspectContext);
  if (!ctx) throw new Error('useInspect must be used within InspectProvider');
  return ctx;
};

export const InspectProvider = ({ children }: { children: ReactNode }) => {
  // Start in the mode the URL deep-links into (?drill=1 inspect, or a
  // ?select=<Component> that implies inspect), so the URL sync (which is
  // one-directional state→URL) never has to fight an initial state that
  // disagrees with the address bar.
  const [mode, setMode] = useState<InspectMode>(() => {
    const params = new URLSearchParams(window.location.search);
    const drill = params.get('drill');
    if (drill === '1' || params.has('select')) return 'inspect';
    return 'view';
  });
  const [current, setCurrent] = useState<Inspection | null>(null);
  const [hovered, setHovered] = useState<Inspection | null>(null);
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);
  // On by default. The curated view is the narrower one — it hides the Stacks and
  // Boxes that hold a screen together, which are half of what a person opens drill
  // mode to look at, and all of what they can tweak. Hide is still there for
  // reading the named components on their own.
  const [showLayout, setShowLayout] = useState(true);

  const active = mode === 'inspect';
  // Promote view→inspect on setActive(true) (e.g. the ?select deep-link poll);
  // setActive(false) returns to view.
  const setActive = useCallback(
    (v: boolean) =>
      setMode((m) => (v ? (m === 'view' ? 'inspect' : m) : 'view')),
    [],
  );

  const value = useMemo(
    () => ({
      mode,
      setMode,
      active,
      setActive,
      current,
      setCurrent,
      hovered,
      setHovered,
      screenEl,
      setScreenEl,
      showLayout,
      setShowLayout,
    }),
    [mode, active, setActive, current, hovered, screenEl, showLayout],
  );

  return (
    <InspectContext.Provider value={value}>{children}</InspectContext.Provider>
  );
};
